import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Send,
  Check,
  ArrowRight,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import api, { getErrorMessage } from '../services/api';
import FeatureLayout from '../components/layout/FeatureLayout';
import SEOHead from '../components/common/SEOHead';

export default function Contact() {
  const { t } = useTranslation('common');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await api.post('/config/contact', {
        name,
        email,
        subject,
        message,
      });

      if (response.data.success) {
        setIsSuccess(true);
        setName('');
        setEmail('');
        setSubject('');
        setMessage('');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FeatureLayout
      breadcrumbs={[
        { label: t('nav.contact') },
      ]}
    >
      <SEOHead
        title="Contact"
        description="Contactez l'équipe Splinity pour toute question sur l'application de gestion de budget pour couples et colocations."
        path="/contact"
      />

      <main className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <div className="pub-ico w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Send className="w-8 h-8" />
            </div>
            <h1 className="pub-title text-3xl font-bold mb-3">
              {t('contact.title')}
            </h1>
            <p className="pub-muted">
              {t('contact.subtitle')}
            </p>
          </div>

          {isSuccess ? (
            <div className="pub-notice pub-notice--ok rounded-xl p-8 text-center animate-fade-in">
              <div className="flex items-center justify-center w-14 h-14 mx-auto mb-4 bg-green-100 rounded-full">
                <Check className="pub-state--ok w-7 h-7" />
              </div>
              <h2 className="pub-title pub-state--ok text-xl font-semibold mb-2">
                {t('contact.successTitle')}
              </h2>
              <p className="pub-muted mb-6">
                {t('contact.successMessage')}
              </p>
              <Link to="/">
                <Button
                  variant="outline"
                  rightIcon={<ArrowRight className="w-4 h-4" />}
                >
                  {t('backToHome')}
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="pub-card p-6 sm:p-8 space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium pub-muted mb-1">
                    {t('contact.nameLabel')}
                  </label>
                  <Input
                    type="text"
                    placeholder={t('contact.namePlaceholder')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium pub-muted mb-1">
                    {t('contact.emailLabel')}
                  </label>
                  <Input
                    type="email"
                    placeholder={t('contact.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium pub-muted mb-1">
                  {t('contact.subjectLabel')}
                </label>
                <Input
                  type="text"
                  placeholder={t('contact.subjectPlaceholder')}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium pub-muted mb-1">
                  {t('contact.messageLabel')}
                </label>
                <textarea
                  placeholder={t('contact.messagePlaceholder')}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={6}
                  className="pub-field w-full rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors resize-vertical"
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                isLoading={isSubmitting}
                rightIcon={<Send className="w-4 h-4" />}
              >
                {t('contact.send')}
              </Button>

              <p className="pub-subtle text-xs text-center">
                {t('contact.directContact')}{' '}
                <a href="mailto:contact@splinity.com" className="text-primary-600 hover:underline">
                  contact@splinity.com
                </a>
              </p>
            </form>
          )}
        </div>
      </main>
    </FeatureLayout>
  );
}
